import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export interface HeroSlide {
  id: string;
  title: string;
  summary: string;
  image: string;
  to: string;
  tag: string;
  meta?: string;
}

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  if (slides.length === 0) return null;

  const go = (dir: number) => setIndex((i) => (i + dir + slides.length) % slides.length);
  const current = slides[index];

  return (
    <section
      className="relative w-full h-[460px] md:h-[600px] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 91.con`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/50 via-background-50/60 to-background-50" />
          <div className="absolute inset-0 bg-background-50/30" />
        </div>
      ))}

      <div className="relative z-10 w-full h-full flex items-center justify-center">
        <div key={index} className="w-full px-4 md:px-8 animate-fade-up">
          <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/15 border border-primary-500/40 text-primary-300 text-xs md:text-sm font-medium">
              <i className="ri-fire-fill text-primary-400" />
              {current.tag}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 mt-4 leading-tight">
              {current.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-700 mt-4 max-w-2xl leading-relaxed line-clamp-2">
              {current.summary}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3 mt-7">
              <Link
                to={current.to}
                className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-play-fill text-lg" />
                立即查看
              </Link>
              <Link
                to="/videos"
                className="h-11 px-6 rounded-md bg-background-100 border border-background-300 text-foreground-900 text-sm font-semibold hover:border-primary-500/60 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-film-line text-lg" />
                浏览视频资源
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/70 text-foreground-900 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/70 text-foreground-900 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-foreground-500/50 hover:bg-foreground-400'
            }`}
          />
        ))}
      </div>
    </section>
  );
}